/**
 * RecoveryCodesPanel — one-shot recovery codes display (HoD §4.3).
 *
 * Renders the 10 recovery codes returned ONCE by ``POST /admin/mfa/enroll``
 * (or the tenant analog). Three load-bearing affordances per HoD:
 *   - 2-column grid of monospace codes (selectable on click).
 *   - "Copy all" button writes the join('\n') to clipboard with a
 *     transient "Copied" state.
 *   - "Download" button creates a Blob text/plain file
 *     ``certain-recovery-codes.txt`` for the user to save.
 *   - Acknowledge-saved checkbox gates the parent's "Continue" button —
 *     the parent reads ``acknowledged`` to enable/disable the next step.
 *
 * Print is OUT of scope for v1 per HoD §9 (deferred to 30j polish).
 */
import { useState } from "react";
import { Button } from "@/components/common/Button";

interface RecoveryCodesPanelProps {
  /** Exactly 10 codes returned by the backend. */
  codes: string[];
  acknowledged: boolean;
  onAcknowledge: (saved: boolean) => void;
}

export function RecoveryCodesPanel({
  codes,
  acknowledged,
  onAcknowledge,
}: RecoveryCodesPanelProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail in iframes / older browsers; the
      // download path remains available so the user is never stuck.
    }
  };

  const handleDownload = (): void => {
    const date = new Date().toISOString().slice(0, 10);
    const body =
      codes.join("\n") + `\n\n--- Generated ${date} for CertAIn ---\n`;
    const blob = new Blob([body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "certain-recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-4">
      <ul
        aria-label="Recovery codes"
        className="grid grid-cols-2 gap-2 rounded-lg border border-brand-200 bg-brand-50/50 p-3 dark:border-white/10 dark:bg-white/[0.02]"
      >
        {codes.map((code) => (
          <li
            key={code}
            className="select-all rounded bg-white px-2 py-1.5 font-mono text-sm tracking-wider text-text-primary dark:bg-white/[0.04] dark:text-text-primary"
          >
            {code}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={handleCopy}
          aria-live="polite"
        >
          {copied ? "Copied" : "Copy all"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={handleDownload}
        >
          Download .txt
        </Button>
      </div>

      <label className="flex items-start gap-2 text-sm text-text-primary dark:text-text-primary">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-brand-300 text-accent-primary focus:ring-brand-500 dark:border-white/20"
          checked={acknowledged}
          onChange={(e) => onAcknowledge(e.target.checked)}
        />
        <span>I&rsquo;ve saved my recovery codes</span>
      </label>
    </div>
  );
}
